import { ReactNode } from "react";
import Link from "../../../../../../../components/link/Link";
import Typography from "../../../../../../../components/typography/Typography";
import { LLMSection, LLMTextLink } from "../@types/LLMSummary";
import styles from "../llm-summary.module.scss";
import { buildPivotUrlForType, buildUrlForType } from "./utils";

type Props = {
  data: LLMSection;
  viewType: "text" | "list";
  title: string;
};

const renderTextWithLinks = (
  text: string,
  links: LLMTextLink[] | undefined,
  textIndex: number
): ReactNode => {
  if (!links) {
    return text;
  }

  const relevantLinks = links
    .filter(link => link.text_idx === textIndex)
    .sort((a, b) => a.start - b.start);

  if (relevantLinks.length === 0) {
    return text;
  }

  const parts: ReactNode[] = [];
  let lastEnd = 0;

  relevantLinks.forEach((link, i) => {
    if (link.start > lastEnd) {
      parts.push(text.slice(lastEnd, link.start));
    }

    const linkText = text.slice(link.start, link.end + 1);
    let url = buildUrlForType(linkText, link.type);
    if (!url) {
      url = buildPivotUrlForType(link.type);
    }
    parts.push(
      url ? (
        <Link key={`link-${i}`} to={url}>
          {linkText}
        </Link>
      ) : (
        <span key={`link-${i}`}>{linkText}</span>
      )
    );

    lastEnd = link.end + 1;
  });

  if (lastEnd < text.length) {
    parts.push(text.slice(lastEnd));
  }

  return parts;
};

const LLMSectionText = ({ data, viewType, title }: Props) => {
  if (!data.text?.length) {
    return null;
  }

  return (
    <section>
      <Typography variant="h5">{title}</Typography>
      {viewType === "text" ? (
        <div className={styles.llm_section_text}>
          {data.text.map((paragraph, index) => (
            <p key={index}>
              {renderTextWithLinks(paragraph, data.links, index)}
            </p>
          ))}
        </div>
      ) : (
        <ul className={styles.llm_section_list}>
          {data.text.map((item, index) => (
            <li key={index}>{renderTextWithLinks(item, data.links, index)}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default LLMSectionText;
